/**
 * Compilation Rules Engine for Flow2
 *
 * Post-compilation rules that fix text quality issues: contrast, vertical centering,
 * callout backgrounds, and text overflow. Rules run after MMOs are generated but
 * before the screen is finalized.
 *
 * @packageDocumentation
 * @module ai/compiler
 */

import Color from '../../model/Color';
import TextFlowObject from '../../model/TextFlowObject';
import ShapeFlowObject, { ShapeType } from '../../model/ShapeFlowObject';
import ImageFlowObject from '../../model/ImageFlowObject';
import FontAttrsArray from '../../model/FontAttrsArray';
import { guidGenerator, getCurrentDate } from '../../features/utils/commonFunctions';

import { ScreenContent, ContentBlock } from '../schemas/ContentBlock';
import { LayoutTemplate, LayoutSlot, frameToPixels } from '../schemas/LayoutTemplate';
import { StyleTheme, getBlockStyle, hexToRgb } from '../schemas/StyleTheme';

import { PretextOverflowRule, PretextVerticalCenterRule } from './PretextRules';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

/**
 * Context passed to each compilation rule for a single screen.
 * Rules can inspect and mutate MMOs in-place.
 */
export interface ScreenCompilationContext {
  screenIndex: number;
  screenContent: ScreenContent;
  screenBackgroundColor: Color;
  decorationMMOs: (ShapeFlowObject | ImageFlowObject)[];
  contentMMOs: (TextFlowObject | ShapeFlowObject | ImageFlowObject)[];
  blockForMMO: Map<string, ContentBlock>;
  slotForMMO: Map<string, LayoutSlot>;
  theme: StyleTheme;
  layout: LayoutTemplate;
  viewSize: { width: number; height: number };
  warnings: string[];
}

/**
 * A single compilation rule that can inspect and mutate MMOs.
 */
export interface CompilationRule {
  name: string;
  priority: number;
  apply(context: ScreenCompilationContext): void;
}

// ---------------------------------------------------------------------------
// Geometry utilities
// ---------------------------------------------------------------------------

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/**
 * What fraction of rect A is covered by rect B (0..1).
 */
function overlapFraction(a: Rect, b: Rect): number {
  if (!rectsOverlap(a, b)) return 0;
  const overlapX = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
  const overlapY = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  const overlapArea = overlapX * overlapY;
  const aArea = a.width * a.height;
  return aArea > 0 ? overlapArea / aArea : 0;
}

// ---------------------------------------------------------------------------
// Color utilities (WCAG)
// ---------------------------------------------------------------------------

/**
 * WCAG relative luminance from sRGB (0..1).
 * Color channels are 0..255.
 */
function getLuminance(c: Color): number {
  const toLinear = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(c.r) + 0.7152 * toLinear(c.g) + 0.0722 * toLinear(c.b);
}

/**
 * WCAG contrast ratio between two colors (1..21).
 */
function getContrastRatio(fg: Color, bg: Color): number {
  const l1 = getLuminance(fg);
  const l2 = getLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Alpha-blend foreground onto background. Both colors should be 0..255 with alpha 0..1.
 */
function alphaBlend(fg: Color, bg: Color): Color {
  const a = fg.a;
  return new Color(
    Math.round(fg.r * a + bg.r * (1 - a)),
    Math.round(fg.g * a + bg.g * (1 - a)),
    Math.round(fg.b * a + bg.b * (1 - a)),
    1
  );
}

/**
 * Determine the effective background color at a target rect by walking the
 * decoration stack (bottom to top).
 */
function getEffectiveBackground(
  targetRect: Rect,
  screenBg: Color,
  decorationMMOs: (ShapeFlowObject | ImageFlowObject)[]
): Color {
  let effective = new Color(screenBg.r, screenBg.g, screenBg.b, 1);

  for (const mmo of decorationMMOs) {
    // Skip ImageFlowObjects, can't sample color from a URL
    if (!(mmo instanceof ShapeFlowObject)) continue;

    const fraction = overlapFraction(targetRect, mmo.frame);
    if (fraction < 0.5) continue;

    const shapeColor = mmo.color;
    const alpha = shapeColor.a;

    if (alpha < 0.3) {
      // Too transparent, skip
      continue;
    } else if (alpha >= 0.8) {
      // Opaque override
      effective = new Color(shapeColor.r, shapeColor.g, shapeColor.b, 1);
    } else {
      // Semi-transparent, alpha blend
      effective = alphaBlend(shapeColor, effective);
    }
  }

  return effective;
}

// ---------------------------------------------------------------------------
// Text estimation utilities
// ---------------------------------------------------------------------------

/**
 * Estimate the number of lines a text string occupies at a given font size
 * within a container of a given width.
 */
function estimateLineCount(text: string, fontSize: number, containerWidth: number): number {
  const avgCharWidth = fontSize * 0.5;
  const charsPerLine = Math.max(1, Math.floor(containerWidth / avgCharWidth));
  let lineCount = 0;

  const paragraphs = text.split('\n');
  for (const para of paragraphs) {
    if (para.length === 0) {
      lineCount += 1;
    } else {
      lineCount += Math.ceil(para.length / charsPerLine);
    }
  }

  return Math.max(1, lineCount);
}

// ---------------------------------------------------------------------------
// Rule 0: ImageOverlayRule (priority 5)
// ---------------------------------------------------------------------------

/**
 * Detects full-bleed images (>70% of screen area) and inserts a 50% black
 * overlay shape between the image and text in contentMMOs.  This ensures
 * white text is readable on top of any image.  Runs BEFORE ContrastRule
 * so that the overlay is visible when contrast is evaluated.
 */
class ImageOverlayRule implements CompilationRule {
  name = 'ImageOverlayRule';
  priority = 5;

  apply(context: ScreenCompilationContext): void {
    const screenArea = context.viewSize.width * context.viewSize.height;

    for (let i = 0; i < context.contentMMOs.length; i++) {
      const mmo = context.contentMMOs[i];
      if (!(mmo instanceof ImageFlowObject)) continue;

      const imgArea = mmo.frame.width * mmo.frame.height;
      if (imgArea / screenArea < 0.7) continue;

      // Create 50% black overlay shape at the same frame as the image
      const [shapeType, pointsArrayString] = ShapeFlowObject.derivePointsArray('rectangle');

      const overlay = new ShapeFlowObject(
        guidGenerator(),
        new Color(0, 0, 0, 0), // borderColor (transparent)
        0,
        null,
        getCurrentDate(),
        { ...mmo.frame },       // same frame as image
        null,
        1,
        null,
        null,
        'shape',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false,                   // locked
        new Color(0, 0, 0, 0.5), // 50% black fill
        shapeType,
        pointsArrayString
      );

      // Insert overlay right after the image so it sits between image and text
      context.contentMMOs.splice(i + 1, 0, overlay);

      // Also add to decorationMMOs so ContrastRule sees it as background
      context.decorationMMOs.push(overlay);

      context.warnings.push(
        `Screen ${context.screenIndex + 1}: ImageOverlayRule added 50% black overlay for full-bleed image`
      );
      break; // Only one full-bleed image per screen
    }
  }
}

// ---------------------------------------------------------------------------
// Rule 1: ContrastRule (priority 10)
// ---------------------------------------------------------------------------

class ContrastRule implements CompilationRule {
  name = 'ContrastRule';
  priority = 10;

  apply(context: ScreenCompilationContext): void {
    for (const mmo of context.contentMMOs) {
      if (!(mmo instanceof TextFlowObject)) continue;

      const effectiveBg = getEffectiveBackground(
        mmo.frame,
        context.screenBackgroundColor,
        context.decorationMMOs
      );

      const textColor = mmo.textColor;
      const fontSize = mmo.font.fontSize;

      // WCAG AA: 4.5:1 for normal text, 3:1 for large text (>=18pt bold or >=24pt)
      const isLargeText = fontSize >= 24 || fontSize >= 18; // simplified: treat >=18 as potentially bold
      const requiredRatio = isLargeText ? 3 : 4.5;

      const ratio = getContrastRatio(textColor, effectiveBg);
      if (ratio >= requiredRatio) continue;

      // Fix: pick white or black depending on background luminance
      const bgLuminance = getLuminance(effectiveBg);
      const newColor = bgLuminance > 0.5 ? Color.black() : Color.white();

      // Update the text object using its built-in method
      mmo.changeColor(newColor);

      context.warnings.push(
        `Screen ${context.screenIndex + 1}: ContrastRule fixed text color (ratio was ${ratio.toFixed(1)}:1, needed ${requiredRatio}:1)`
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Rule 2: VerticalCenterRule (priority 20)
// ---------------------------------------------------------------------------

class VerticalCenterRule implements CompilationRule {
  name = 'VerticalCenterRule';
  priority = 20;

  apply(context: ScreenCompilationContext): void {
    for (const mmo of context.contentMMOs) {
      if (!(mmo instanceof TextFlowObject)) continue;

      // Skip MMOs from blocks that handle their own positioning (toc-list, timeline)
      const block = context.blockForMMO.get(mmo.id);
      if (block && (block.type === 'toc-list' || block.type === 'timeline')) continue;

      const slot = context.slotForMMO.get(mmo.id);
      if (!slot) continue;

      const slotFrame = frameToPixels(slot.frame, context.viewSize);
      const fontSize = mmo.font.fontSize;
      const lineHeight = context.theme.typography.lineHeight || 1.4;

      const lineCount = estimateLineCount(mmo.text, fontSize, slotFrame.width);
      const estimatedTextHeight = lineCount * fontSize * lineHeight;
      const slack = slotFrame.height - estimatedTextHeight;

      // Only center if slack > 20% of slot height AND > 20px
      if (slack <= slotFrame.height * 0.2 || slack <= 20) continue;

      const shift = slack / 2;
      mmo.frame.y = slotFrame.y + shift;
      mmo.frame.height = estimatedTextHeight;
    }
  }
}

// ---------------------------------------------------------------------------
// Rule 3: CalloutBackgroundRule (priority 30)
// ---------------------------------------------------------------------------

class CalloutBackgroundRule implements CompilationRule {
  name = 'CalloutBackgroundRule';
  priority = 30;

  apply(context: ScreenCompilationContext): void {
    for (const mmo of context.contentMMOs) {
      if (!(mmo instanceof TextFlowObject)) continue;

      const block = context.blockForMMO.get(mmo.id);
      if (!block || block.type !== 'callout') continue;

      const style = getBlockStyle(context.theme, 'callout');
      if (!style.backgroundColor) continue;

      const bgRgb = hexToRgb(style.backgroundColor);
      if (!bgRgb) continue;

      const padding = typeof style.padding === 'number' ? style.padding : 16;
      const cornerRadius = style.borderRadius || 8;

      const bgColor = new Color(bgRgb.r, bgRgb.g, bgRgb.b, 0.9);

      const [shapeType, pointsArrayString] = ShapeFlowObject.derivePointsArray('roundedRectangle');

      const shape = new ShapeFlowObject(
        guidGenerator(),
        new Color(0, 0, 0, 0), // borderColor
        0,
        null,
        getCurrentDate(),
        {
          x: mmo.frame.x - padding,
          y: mmo.frame.y - padding,
          width: mmo.frame.width + padding * 2,
          height: mmo.frame.height + padding * 2,
        },
        null,
        1,
        null,
        null,
        'shape',
        null,
        null,
        0,
        { height: 0, width: 0 },
        null,
        null,
        getCurrentDate(),
        false, // locked
        bgColor,
        shapeType,
        pointsArrayString,
        cornerRadius
      );

      // Insert into decorationMMOs (behind content, above other decorations)
      context.decorationMMOs.push(shape);
    }
  }
}

// ---------------------------------------------------------------------------
// Rule 4: OverflowRule (priority 40)
// ---------------------------------------------------------------------------

class OverflowRule implements CompilationRule {
  name = 'OverflowRule';
  priority = 40;

  apply(context: ScreenCompilationContext): void {
    for (const mmo of context.contentMMOs) {
      if (!(mmo instanceof TextFlowObject)) continue;

      // Skip MMOs from blocks that handle their own sizing (toc-list, timeline)
      const block = context.blockForMMO.get(mmo.id);
      if (block && (block.type === 'toc-list' || block.type === 'timeline')) continue;

      const slot = context.slotForMMO.get(mmo.id);
      if (!slot) continue;

      const slotFrame = frameToPixels(slot.frame, context.viewSize);
      const lineHeight = context.theme.typography.lineHeight || 1.4;
      const minFontSize = slot.constraints?.minFontSize || 12;

      let fontSize = mmo.font.fontSize;
      let estimatedHeight = estimateLineCount(mmo.text, fontSize, slotFrame.width) * fontSize * lineHeight;

      // Reduce font size by 1pt iteratively until text fits
      while (estimatedHeight > slotFrame.height && fontSize > minFontSize) {
        fontSize -= 1;
        estimatedHeight = estimateLineCount(mmo.text, fontSize, slotFrame.width) * fontSize * lineHeight;
      }

      if (fontSize === mmo.font.fontSize) continue;

      // Clamp to floor
      fontSize = Math.max(fontSize, minFontSize);

      // Update font size using the built-in method
      mmo.changeFontSize(fontSize);

      context.warnings.push(
        `Screen ${context.screenIndex + 1}: OverflowRule reduced font from ${mmo.font.fontSize}pt to ${fontSize}pt`
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

export const DEFAULT_COMPILATION_RULES: CompilationRule[] = [
  new ImageOverlayRule(),
  new ContrastRule(),
  new PretextVerticalCenterRule(),
  new CalloutBackgroundRule(),
  new PretextOverflowRule(),
];

/**
 * Run all compilation rules against a screen context.
 * Rules run in priority order (lowest first). Each rule is wrapped in
 * try/catch, errors become warnings, never crash compilation.
 */
export function runCompilationRules(
  context: ScreenCompilationContext,
  rules: CompilationRule[] = DEFAULT_COMPILATION_RULES
): void {
  const sorted = [...rules].sort((a, b) => a.priority - b.priority);

  for (const rule of sorted) {
    try {
      rule.apply(context);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      context.warnings.push(
        `Screen ${context.screenIndex + 1}: Rule "${rule.name}" failed: ${message}`
      );
    }
  }
}
